import type {
  CountryCode,
  CurrencyCode,
  MoneyV2,
} from '@shopify/hydrogen/storefront-api-types';
import fx from 'money';
import {currencyDetails} from '~/data/currencies';

export interface Currency {
  _key: string;
  _type: string;
  currency: string;
  exchange_rates: ExchangeRate[];
  details: {
    country: string;
    currency_code: string;
    image: string;
    currency: {
      symbol: string;
      name: string;
      symbol_native: string;
      decimal_digits: number;
      rounding: number;
      code: string;
      name_plural: string;
    };
  };
}

export interface ExchangeRate {
  _key: string;
  _type: string;
  base_currency: string;
  rate: number;
}

export interface CurrencySettings {
  all_currencies: Pick<
    Currency,
    '_key' | '_type' | 'currency' | 'exchange_rates'
  >[];
  base_currencies: Pick<Currency, '_key' | '_type' | 'currency'>[] & {
    country: CountryCode;
  };
}

export interface CurrencySettingsGlobal {
  exchangeRates: {[x: string]: number};
  hkExchangeRates: {[x: string]: number};
  twExchangeRates: {[x: string]: number};
  baseCurrency: string;
  all_currencies: Currency[];
  selectedCurrency: string;
}

export const getExchangeRates = (
  currencySettings: CurrencySettings,
  baseCurrency: string,
) =>
  currencySettings.all_currencies.map((c) => {
    const currency = c.currency.split('-')[1];
    const exchangeRate = c.exchange_rates.find(
      (er) => er.base_currency.split('-')[1] === baseCurrency,
    )?.rate;
    return {
      currency,
      rate: exchangeRate,
    };
  });

export const getExchangeRatesMap = (exchangeRates: any) =>
  exchangeRates.reduce(
    (
      a: {currency: string; rate: number}[],
      v: {currency: string; rate: number},
    ) => ({
      ...a,
      [v.currency]: v.rate,
    }),
    {},
  );

/** A money value together with the currency its amount is actually in. */
export interface DisplayPrice {
  amount: number;
  currencyCode: string;
}

/**
 * Rate tables are stored per base currency. Shopify prices this shop in HKD,
 * but the TW storefront's base is TWD, so the table to use is chosen by the
 * money's OWN currency rather than by the storefront — that way a price
 * converts identically on www and on tw.
 */
const RATES_BY_SOURCE: Record<string, keyof CurrencySettingsGlobal> = {
  HKD: 'hkExchangeRates',
  TWD: 'twExchangeRates',
};

/**
 * Convert a Shopify money value into the currency the shopper picked.
 *
 * The conversion starts from the money's own `currencyCode`, never from the
 * storefront's base currency. Those two are the same on www.helmetking.com
 * (base HKD, Shopify prices in HKD) and different on tw.helmetking.com, whose
 * base is TWD while Shopify still prices in HKD. Reading the base as the
 * source meant HKD 1,958 was converted "TWD to TWD" and displayed as
 * TWD 1,958 instead of TWD 7,549 — and the same 3.86x understatement hit
 * every other currency on that storefront, because they all converted from
 * the wrong starting point. A shopper saw the low number and was charged the
 * real one at Shopify checkout.
 *
 * Returning the currency the amount is actually in is what stops the bug
 * class rather than this one instance: a caller can no longer print a number
 * under a currency it was never converted to. When no rate is available the
 * money comes back in its own currency, which is truthful, instead of being
 * relabelled.
 */
export const formatCurrency = ({
  price,
  currency,
}: {
  price: MoneyV2 | undefined | null;
  currency: CurrencySettingsGlobal;
}): DisplayPrice | null => {
  const amount = parseFloat(price?.amount ?? '');
  if (!price || !Number.isFinite(amount)) return null;

  const from = price.currencyCode || currency.baseCurrency || 'HKD';
  const to = currency.selectedCurrency;
  // parseFloat, not parseInt: cents are part of the price.
  if (from === to) return {amount, currencyCode: to};

  const sourceTable = RATES_BY_SOURCE[from];
  const rates =
    (sourceTable
      ? (currency[sourceTable] as {[x: string]: number})
      : currency.exchangeRates) ?? currency.exchangeRates;
  const base = sourceTable ? from : currency.baseCurrency;

  // fx.convert divides by rates[from] and multiplies by rates[to]; a missing
  // entry would otherwise throw, or worse, quietly produce a number in no
  // currency at all.
  if (!rates || !rates[from] || !rates[to]) {
    return {amount, currencyCode: from};
  }

  fx.base = base;
  fx.rates = rates;
  return {amount: fx.convert(amount, {from, to}), currencyCode: to};
};
